import React from "react";
import i18n from "i18next";
import { initReactI18next, useTranslation as useI18nextTranslation } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import type { Locale } from "antd/es/locale";
import antdZhCN from "antd/locale/zh_CN";
import antdEnUS from "antd/locale/en_US";
import antdZhTW from "antd/locale/zh_TW";
import antdJaJP from "antd/locale/ja_JP";
import antdKoKR from "antd/locale/ko_KR";
import zhCN from "./locales/zh-CN";
import enUS from "./locales/en-US";
import zhTW from "./locales/zh-TW";
import jaJP from "./locales/ja-JP";
import koKR from "./locales/ko-KR";

// PRD v6 多语言:简体中文 / 繁体中文 / 英语 / 日语 / 韩语。
export type Language = "zh-CN" | "zh-TW" | "en-US" | "ja-JP" | "ko-KR";

export const SUPPORTED_LANGUAGES: { value: Language; label: string }[] = [
  { value: "zh-CN", label: "简体中文" },
  { value: "zh-TW", label: "繁體中文" },
  { value: "en-US", label: "English" },
  { value: "ja-JP", label: "日本語" },
  { value: "ko-KR", label: "한국어" },
];

const ANTD_LOCALES: Record<Language, Locale> = {
  "zh-CN": antdZhCN,
  "zh-TW": antdZhTW,
  "en-US": antdEnUS,
  "ja-JP": antdJaJP,
  "ko-KR": antdKoKR,
};

const STORAGE_KEY = "hongmo_lang";

// 初始化 i18next(单例)。资源放在 translation 命名空间,key 用点路径,插值用 {{var}}。
if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        "zh-CN": { translation: zhCN },
        "zh-TW": { translation: zhTW },
        "en-US": { translation: enUS },
        "ja-JP": { translation: jaJP },
        "ko-KR": { translation: koKR },
      },
      fallbackLng: "zh-CN",
      supportedLngs: ["zh-CN", "zh-TW", "en-US", "ja-JP", "ko-KR"],
      load: "currentOnly",
      interpolation: { escapeValue: false },
      detection: {
        order: ["localStorage", "navigator"],
        lookupLocalStorage: STORAGE_KEY,
        caches: ["localStorage"],
      },
      returnNull: false,
      // 资源已打包同步加载,无需 Suspense;关闭可避免无 Suspense 边界时白屏。
      react: { useSuspense: false },
    });
}

function normalizeLang(raw?: string): Language {
  const v = (raw || "").toLowerCase().replace(/_/g, "-");
  if (v.startsWith("zh-tw") || v.startsWith("zh-hant") || v.startsWith("zh-hk") || v.startsWith("zh-mo")) return "zh-TW";
  if (v.startsWith("ja")) return "ja-JP";
  if (v.startsWith("ko")) return "ko-KR";
  if (v.startsWith("en")) return "en-US";
  return "zh-CN";
}

// LanguageProvider:react-i18next 基于单例无需 Provider,这里保留为薄包装,
// 仅确保 i18n 已初始化并同步 <html lang>。
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  React.useEffect(() => {
    document.documentElement.setAttribute("lang", normalizeLang(i18n.language));
  }, []);
  return <>{children}</>;
};

export interface UseTranslationResult {
  t: (key: string, params?: Record<string, unknown>) => string;
  lang: Language;
  setLang: (lang: Language) => void;
  antdLocale: Locale;
}

// useTranslation:在 react-i18next 之上保持本项目既有的 { t, lang, setLang, antdLocale } 形态,
// 既是真正的 i18next,又不破坏已接入的调用点。
export function useTranslation(): UseTranslationResult {
  const { t, i18n: instance } = useI18nextTranslation();
  const lang = normalizeLang(instance.language);
  const translate = React.useCallback(
    (key: string, params?: Record<string, unknown>) => t(key, params ?? undefined) as string,
    [t]
  );
  const setLang = React.useCallback(
    (next: Language) => {
      instance.changeLanguage(next);
      document.documentElement.setAttribute("lang", next);
    },
    [instance]
  );
  return React.useMemo(
    () => ({ t: translate, lang, setLang, antdLocale: ANTD_LOCALES[lang] ?? antdZhCN }),
    [translate, lang, setLang]
  );
}

export default i18n;
